/* ==========================================================================
   Abbara — mode-aware currency converter + Location/Currency tandem selector
   --------------------------------------------------------------------------
   Reads window.abbaraCurrency (emitted by snippets/currency-config.liquid).

   • mode === 'native'  → Shopify Payments is presenting REAL local currencies.
       The converter stays INERT (server prices are authoritative). The selector
       drives Shopify's native localization form. abbaraMoney.format() passes
       through. Flip happens automatically — no code change.
   • mode === 'cosmetic' → store charges in one currency (AED). We convert the
       DISPLAYED prices to the visitor's currency for reference only; checkout
       still charges the base currency. A disclaimer says so.

   Prices are wrapped by snippets/money.liquid as <span class="money"
   data-cents="<AED minor units>">. We convert from base, never from the
   on-screen text, so re-picks + restore-to-base are lossless.
   ========================================================================== */
(function () {
  'use strict';

  var CFG = window.abbaraCurrency;
  if (!CFG || CFG.enabled === false) return;

  var NATIVE = CFG.mode === 'native';
  var BASE   = CFG.base || 'AED';

  /* ---- currency metadata ------------------------------------------------ */
  var THREE_DECIMAL = { KWD: 1, BHD: 1, OMR: 1 };
  function decimals(ccy) { return THREE_DECIMAL[ccy] ? 3 : 2; }

  // Country → display currency. In cosmetic mode Shopify reports the store
  // currency for every country, so we keep our own ISO→currency table.
  var COUNTRY_CCY = {
    AE: 'AED', SA: 'SAR', KW: 'KWD', QA: 'QAR', BH: 'BHD', OM: 'OMR', IN: 'INR',
    US: 'USD', GB: 'GBP', CA: 'CAD', AU: 'AUD', CH: 'CHF', JP: 'JPY',
    AT: 'EUR', BE: 'EUR', HR: 'EUR', CY: 'EUR', EE: 'EUR', FI: 'EUR', FR: 'EUR',
    DE: 'EUR', GR: 'EUR', IE: 'EUR', IT: 'EUR', LV: 'EUR', LT: 'EUR', LU: 'EUR',
    MT: 'EUR', NL: 'EUR', PT: 'EUR', SK: 'EUR', SI: 'EUR', ES: 'EUR'
  };

  // The currency list is DERIVED from the configured markets so it always
  // matches the available Locations (one currency per country). This is why
  // selecting India offers INR and EUR never appears unless an EU market exists.
  var SUPPORTED = [BASE];
  (CFG.countries || []).forEach(function (c) {
    var cc = ccyForCountry(c.iso);
    if (SUPPORTED.indexOf(cc) === -1) SUPPORTED.push(cc);
  });
  if (SUPPORTED.length === 1 && CFG.supported) {
    CFG.supported.forEach(function (c) { if (SUPPORTED.indexOf(c) === -1) SUPPORTED.push(c); });
  }

  /* ---- state ------------------------------------------------------------ */
  var LS_PREF  = 'abbara-ccy-pref';   // { ccy, country, manual }
  var LS_RATES = 'abbara-fx';         // { ts, base, rates }
  var RATE_TTL = 18 * 3600 * 1000;    // 18h

  var rates = {};                     // base -> ccy multiplier
  var active = BASE;
  var activeCountry = CFG.activeCountry || 'AE';

  function num(n) { n = parseFloat(n); return isFinite(n) ? n : null; }

  // seed with manual rates from settings (base always = 1)
  rates[BASE] = 1;
  (function () {
    var m = CFG.manualRates || {};
    for (var k in m) if (num(m[k]) != null) rates[k] = num(m[k]);
  })();

  // country iso -> display name (from Shopify's available_countries)
  var COUNTRY_NAME = {};
  (CFG.countries || []).forEach(function (c) { if (c && c.iso) COUNTRY_NAME[c.iso] = c.name; });

  // display currency for a country, by mode
  function ccyForCountry(iso) {
    if (NATIVE && CFG.countryCurrency && CFG.countryCurrency[iso]) return CFG.countryCurrency[iso];
    return COUNTRY_CCY[iso] || BASE;
  }

  /* ---- persistence ------------------------------------------------------ */
  function readPref()  { try { return JSON.parse(localStorage.getItem(LS_PREF) || 'null'); } catch (e) { return null; } }
  function writePref(p){ try { localStorage.setItem(LS_PREF, JSON.stringify(p)); } catch (e) {} }

  /* ---- exchange rates --------------------------------------------------- */
  function mergeRates(r) { for (var k in r) if (num(r[k]) != null) rates[k] = num(r[k]); }

  function loadCachedRates() {
    try {
      var c = JSON.parse(localStorage.getItem(LS_RATES) || 'null');
      if (c && c.base === BASE && c.rates && (Date.now() - c.ts) < RATE_TTL) { mergeRates(c.rates); return true; }
    } catch (e) {}
    return false;
  }

  function fetchLiveRates() {
    if (!CFG.liveRates || !CFG.rateApi) return Promise.resolve(false);
    if (loadCachedRates()) return Promise.resolve(true);
    return fetch(CFG.rateApi, { mode: 'cors' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) {
        var r = j && (j.rates || j.conversion_rates);
        if (!r) return false;
        mergeRates(r);
        try { localStorage.setItem(LS_RATES, JSON.stringify({ ts: Date.now(), base: BASE, rates: r })); } catch (e) {}
        return true;
      })
      .catch(function () { return false; });
  }

  /* ---- formatting ------------------------------------------------------- */
  function intl(value, ccy) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: ccy,
        minimumFractionDigits: decimals(ccy), maximumFractionDigits: decimals(ccy)
      }).format(value);
    } catch (e) { return ccy + ' ' + value.toFixed(decimals(ccy)); }
  }

  // Whole-number currency format — no decimals. Premium look: prices read clean
  // (₹8,999, not ₹8,999.78).
  function intl0(value, ccy) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency', currency: ccy,
        minimumFractionDigits: 0, maximumFractionDigits: 0
      }).format(value);
    } catch (e) { return ccy + ' ' + Math.round(value).toLocaleString(); }
  }

  // Drop the ".00" decimals from the store-rendered base price, keeping its money
  // format (e.g. "Dhs 8,999") so the base currency reads clean too.
  function stripDecimals(html) {
    return String(html == null ? '' : html).replace(/\.\d{2}(?=\D|$)/g, '');
  }

  // centsBase = amount in BASE minor units (AED fils). In cosmetic mode the
  // converted amount is rounded DOWN (floored) to a whole unit and shown with no
  // decimals. Native mode is left untouched (real charged amounts).
  function formatActive(centsBase) {
    var amount = (parseFloat(centsBase) || 0) / 100;
    if (NATIVE) return intl(amount, BASE);
    var ccy = BASE;
    if (active !== BASE && rates[active] != null) { amount = amount * rates[active]; ccy = active; }
    return intl0(Math.floor(amount), ccy);
  }

  /* ---- DOM conversion --------------------------------------------------- */
  function convertDom(root) {
    if (NATIVE) return;
    root = root || document;
    if (!root.querySelectorAll) return;
    var nodes = root.querySelectorAll('.money[data-cents]');
    for (var i = 0; i < nodes.length; i++) {
      var el = nodes[i];
      if (el.getAttribute('data-base-html') == null) el.setAttribute('data-base-html', el.innerHTML);
      var cents = parseInt(el.getAttribute('data-cents'), 10);
      if (!isFinite(cents)) continue;
      if (active === BASE) el.innerHTML = stripDecimals(el.getAttribute('data-base-html'));
      else el.textContent = formatActive(cents);
    }
  }

  /* ---- public API (used by pdp.js / bundle.js / theme.js formatters) ---- */
  window.abbaraMoney = {
    format: function (centsBase) { return formatActive(centsBase); },
    convert: convertDom,
    active: function () { return active; },
    base: BASE,
    setCurrency: setCurrency
  };

  function emitChange() {
    try { document.dispatchEvent(new CustomEvent('abbara:currencychange', { detail: { currency: active, base: BASE } })); } catch (e) {}
    updateDisclaimers();
    updateLabels();
    syncMenus();
    convertPriceFilter();
  }

  function setCurrency(ccy, opts) {
    opts = opts || {};
    if (SUPPORTED.indexOf(ccy) === -1) ccy = BASE;
    active = ccy;
    if (opts.country) activeCountry = opts.country;
    if (opts.persist !== false) writePref({ ccy: active, country: activeCountry, manual: !!opts.manual });
    if (active !== BASE && rates[active] == null && CFG.liveRates) {
      fetchLiveRates().then(function () { convertDom(document); emitChange(); });
    }
    convertDom(document);
    emitChange();
  }

  /* ---- disclaimer toggle ------------------------------------------------ */
  function updateDisclaimers() {
    var show = !NATIVE && active !== BASE;
    var els = document.querySelectorAll('[data-currency-disclaimer]');
    for (var i = 0; i < els.length; i++) els[i].hidden = !show;
  }

  /* ---- collection / search price filter --------------------------------
     The filter operates on the REAL base (AED) prices, but we show it in the
     active display currency (floored, no decimals) and convert the shopper's
     input back to base on submit so Shopify still filters correctly. */
  function numOf(s) { var n = parseFloat(String(s == null ? '' : s).replace(/[^\d.]/g, '')); return isFinite(n) ? n : null; }

  function convertPriceFilter() {
    if (NATIVE) return;
    var inputs = document.querySelectorAll('[data-price-filter]');
    if (!inputs.length) return;
    var rate = (active !== BASE && rates[active] != null) ? rates[active] : 1;
    inputs.forEach(function (inp) {
      // capture the base (AED) placeholder + value once, before any conversion
      if (inp.dataset.aedPh == null) inp.dataset.aedPh = inp.getAttribute('placeholder') || '';
      if (inp.dataset.aedVal == null) inp.dataset.aedVal = inp.value || '';
      var ph = numOf(inp.dataset.aedPh);
      if (ph != null) inp.setAttribute('placeholder', String(Math.floor(ph * rate)));
      var bv = numOf(inp.dataset.aedVal);
      inp.value = (inp.dataset.aedVal !== '' && bv != null) ? String(Math.floor(bv * rate)) : '';
    });
    // a small "Prices in XXX" hint + bind the form to convert back on submit
    document.querySelectorAll('.plp-price-range').forEach(function (box) {
      var hint = box.querySelector('[data-price-ccy]');
      if (!hint) {
        hint = document.createElement('div');
        hint.className = 'plp-price-ccy';
        hint.setAttribute('data-price-ccy', '');
        box.insertBefore(hint, box.firstChild);
      }
      hint.textContent = 'Prices in ' + active;
      var form = box.closest('form');
      if (form && form.dataset.ccyPriceBound !== '1') {
        form.dataset.ccyPriceBound = '1';
        form.addEventListener('submit', function () {
          if (active === BASE) return;
          var r = rates[active] != null ? rates[active] : 1;
          box.querySelectorAll('[data-price-filter]').forEach(function (inp) {
            var v = numOf(inp.value);
            if (inp.value !== '' && v != null) inp.value = (v / r).toFixed(2); // back to base for Shopify
          });
        });
      }
    });
  }

  /* ---- tandem Location / Currency selector ------------------------------ */
  function buildCurrencyMenu(menu) {
    if (!menu || menu.dataset.built === '1') return;
    var html = '';
    SUPPORTED.forEach(function (ccy) {
      html += '<button type="button" class="curreg__opt" data-ccy-option data-currency="' + ccy + '"' +
              ' aria-current="' + (ccy === active ? 'true' : 'false') + '"><span>' + ccy + '</span></button>';
    });
    menu.innerHTML = html;
    menu.dataset.built = '1';
  }

  // Both menus show their full list; selecting either one syncs the other (a
  // currency maps to its primary country and vice-versa), so they can never be
  // mismatched. Just mark the active currency.
  function filterCurrencyMenu() {
    document.querySelectorAll('[data-ccy-option]').forEach(function (o) {
      o.hidden = false;
      o.setAttribute('aria-current', o.getAttribute('data-currency') === active ? 'true' : 'false');
    });
  }

  function countryForCurrency(ccy) {
    var list = CFG.countries || [];
    for (var i = 0; i < list.length; i++) if (ccyForCountry(list[i].iso) === ccy) return list[i].iso;
    return null;
  }

  function updateLabels() {
    document.querySelectorAll('[data-ccy-label]').forEach(function (l) { l.textContent = active; });
    document.querySelectorAll('[data-loc-label]').forEach(function (l) {
      l.textContent = COUNTRY_NAME[activeCountry] || activeCountry;
    });
    document.querySelectorAll('[data-loc-option]').forEach(function (o) {
      o.setAttribute('aria-current', o.getAttribute('data-country') === activeCountry ? 'true' : 'false');
    });
  }

  function syncMenus() { filterCurrencyMenu(); }

  function closeMenus(except) {
    document.querySelectorAll('[data-curreg-menu].is-open').forEach(function (m) { if (m !== except) m.classList.remove('is-open'); });
  }

  function bindSelector() {
    var roots = document.querySelectorAll('[data-currency-switcher]');
    for (var i = 0; i < roots.length; i++) {
      var root = roots[i];
      if (root.dataset.bound === '1') continue;
      root.dataset.bound = '1';

      var ccyMenu = root.querySelector('[data-ccy-menu]');
      buildCurrencyMenu(ccyMenu);

      // toggles open their own menu
      root.querySelectorAll('[data-curreg-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function (e) {
          e.preventDefault(); e.stopPropagation();
          var menu = btn.parentNode.querySelector('[data-curreg-menu]');
          if (!menu) return;
          var open = menu.classList.contains('is-open');
          closeMenus(menu);
          menu.classList.toggle('is-open', !open);
        });
      });

      root.addEventListener('click', function (e) {
        var c = e.target.closest && e.target.closest('[data-ccy-option]');
        if (c) {
          e.preventDefault();
          closeMenus();
          var ccy = c.getAttribute('data-currency');
          if (NATIVE) { submitForCurrency(root, ccy); return; }
          setCurrency(ccy, { country: countryForCurrency(ccy) || activeCountry, manual: true });
          return;
        }
        var l = e.target.closest && e.target.closest('[data-loc-option]');
        if (l) {
          e.preventDefault();
          closeMenus();
          var country = l.getAttribute('data-country');
          if (NATIVE) { submitLocation(root, country); return; }
          // cosmetic: no page reload — set the country + its currency and convert in place
          setCurrency(ccyForCountry(country), { country: country, manual: true });
        }
      });

      filterCurrencyMenu();
    }
    updateLabels();
  }

  function submitLocation(root, country) {
    var form  = root.querySelector('form');
    var input = root.querySelector('[data-loc-country-input]');
    if (form && input) { input.value = country; form.submit(); }
  }

  // native mode: pick the first country that uses this currency, submit
  function submitForCurrency(root, ccy) {
    var opts = root.querySelectorAll('[data-loc-option]');
    for (var i = 0; i < opts.length; i++) {
      if (opts[i].getAttribute('data-currency') === ccy) { submitLocation(root, opts[i].getAttribute('data-country')); return; }
    }
  }

  /* ---- geolocation auto-detect (first visit only) ----------------------- */
  function geoDetect() {
    if (!CFG.geoAutodetect) return;
    var done = false;
    var applied = function (cc) {
      if (done || !cc) return; done = true;
      cc = String(cc).toUpperCase();
      if (NATIVE) return; // don't auto-reload markets; user can pick
      var ccy = COUNTRY_CCY[cc];
      if (ccy && SUPPORTED.indexOf(ccy) !== -1) setCurrency(ccy, { country: cc, manual: false });
    };
    fetch('https://ipapi.co/json/').then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { applied(j && (j.country_code || j.country)); })
      .catch(function () {})
      .then(function () {
        if (done) return;
        return fetch('https://www.cloudflare.com/cdn-cgi/trace').then(function (r) { return r.ok ? r.text() : ''; })
          .then(function (t) { var m = /loc=([A-Z]{2})/.exec(t || ''); if (m) applied(m[1]); })
          .catch(function () {});
      });
  }

  /* ---- dynamic re-render (AJAX-injected price nodes) -------------------- */
  function observeDynamic() {
    if (NATIVE || !window.MutationObserver) return;
    var targets = document.querySelectorAll('[data-products-grid], [data-search-results], main');
    var obs = new MutationObserver(function (muts) {
      for (var i = 0; i < muts.length; i++) {
        var added = muts[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var n = added[j];
          if (n.nodeType !== 1) continue;
          if (n.matches && n.matches('.money[data-cents]')) convertDom(n.parentNode);
          else if (n.querySelector && n.querySelector('.money[data-cents]')) convertDom(n);
        }
      }
    });
    for (var k = 0; k < targets.length; k++) obs.observe(targets[k], { childList: true, subtree: true });
  }

  /* ---- init ------------------------------------------------------------- */
  function init() {
    var pref = readPref();
    if (pref && pref.ccy && SUPPORTED.indexOf(pref.ccy) !== -1) {
      active = pref.ccy;
      activeCountry = pref.country || activeCountry;
    }
    loadCachedRates();
    convertDom(document);
    bindSelector();
    updateDisclaimers();
    updateLabels();
    syncMenus();
    convertPriceFilter();

    // close menus on outside click
    document.addEventListener('mousedown', function (e) {
      if (!e.target.closest || !e.target.closest('[data-currency-switcher]')) closeMenus();
    });

    // warm / refresh live rates, then re-convert if we're showing a non-base currency
    fetchLiveRates().then(function (ok) { if (ok && active !== BASE) { convertDom(document); convertPriceFilter(); } });

    if (!pref) geoDetect();
    observeDynamic();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
